import { Injectable, inject } from '@angular/core';
import { Subject, Observable, forkJoin, of, concat, zip } from 'rxjs';
import { HttpParams, HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { debounceTime, switchMap, first, map, reduce } from 'rxjs/operators';
import {
  CategoriesGroup,
  Category,
  Event,
  EventPersonalization,
  PlatformCategories,
  TicketTemplate,
  ValidTicket,
  Platforms,
  IWeezeventRate,
  EventCustomFaq,
  Organizer,
} from '../../models';
import {
  DashboardEventService,
  DashboardWaitListService,
} from '../../shared-dashboard/services';
import { GeocodingService } from './geocoding.service';
import { NotificationService, UploaderService } from '../../services';
import { AutomationService } from './index';

@Injectable({
  providedIn: 'root',
})
export class AdminEventService {
  private http = inject(HttpClient);
  private dashboardEventService = inject(DashboardEventService);
  private geocodingService = inject(GeocodingService);
  private notificationService = inject(NotificationService);
  private uploaderService = inject(UploaderService);
  private dashboardWaiListService = inject(DashboardWaitListService);
  private automationService = inject(AutomationService);

  configUrl = environment.apiUrl;

  public getMoreEvents = new Subject<{limit: number, offset: number, searchQuery?: string}>();

  public getMoreParticipants = new Subject<{limit: number, offset: number, searchQuery: string}>();

  getParticipantList(eventId: number) {
    const route = `${this.configUrl}e/a/${eventId}/export/accessControlList/`;
    return this.getMoreParticipants.pipe(
      // wait 600ms after each keystroke before considering the term
      debounceTime(600),
      // switch to new search observable each time the term changes
      switchMap( ({limit, offset, searchQuery}) => {
        const params = new HttpParams({
          fromObject: {
            limit: limit.toString(),
            offset: offset.toString(),
            searchQuery: encodeURIComponent(searchQuery.toString()),
          },
        });
        return this.http.get<{
          tickets: {
            rows: [],
            count: number
          },
          categories: {
            rows: {
              id: number,
              platformName: string,
              eventId: number,
              updatedAt: Date,
              createdAt: Date,
              categoryId: number,
              ticketsNb: number,
            }[],
            count: number,
          }
        }>(route, {params});
      }),
    );
  }

  deleteParticipants(eventId: number, ticketsToDelete: Array<{
    barcode: string;
    platformCategoryName: string;
  }>): Observable<any> {
    const route = `${this.configUrl}e/a/${eventId}/delete/accessControlList`;
    return this.http.put<any>(route, {
      ticketsToDelete,
    });
  }

  createEvent(event: Event) {
    const route = `${this.configUrl}e/a/events`;
    return this.http.post(route, event).pipe(
      first(),
      map( (e) => new Event(e)),
    );
  }

  createEventCopy(event: Event, sectionsToCopy: {[key: string]: boolean}, oldEventId: number) {
    const route = `${this.configUrl}e/a/eventsCopy`;
    return this.http.post(route, {event, sectionsToCopy, oldEventId}).pipe(
      first(),
    );
  }

  /**
   * Creating a complete event requires 4 steps in order to build all models
   *
   * 1 Get or create postal address, upload event cover and seating plan if any
   *
   * 2 Event model : this steps also creates Organizers and Waitlistconfig
   *
   * 3 EventPersonalization, TicketingPlatforms, TicketTemplates, Waitlistconfig
   *
   * 4 categories group
   *
   * 5 categories <= categories requires id from groups and templates
   *
   * 6 automations
   *
   * @param event Event with joined models
   * @returns event
   */
  createEventFull(event: Event, platform: Platforms) {
    const eventForeignId = event.TicketingPlatforms?.[0]?.platformEventId;
    const ticketingPlatforms = event.TicketingPlatforms ?? [];
    delete event.TicketingPlatforms;
    const categories = event.Categories ?? [];
    delete event.Categories;
    const categoriesGroup = event.CategoriesGroups ?? [];
    delete event.CategoriesGroups;
    const ticketTemplates = event.TicketTemplates ?? [];
    delete event.TicketTemplates;
    const eventPersonalization = event.EventPersonalization ?? new EventPersonalization({});
    delete event.EventPersonalization;
    const imgUrlFileName = event.imgUrl?.split('/').pop() ?? `${platform}-id-${eventForeignId}-cover`;
    const seatingPlanUrlFileName = event.seatingPlanUrl?.split('/').pop() ?? `${platform}-id-${eventForeignId}-plan`;

    const postalAddressQuery = [
      event.PostalAddress.address1,
      event.PostalAddress.address2,
      event.PostalAddress.city,
      event.PostalAddress.zip_code,
      event.PostalAddress.state,
      event.PostalAddress.country,
    ].filter( (x) => !!x).join(', ');

    return forkJoin({
      postalAddress: this.geocodingService.createLocationIfNotExist(postalAddressQuery),
      imgUrl: event.imgUrl ? this.uploaderService.postImageUrl(event.imgUrl, imgUrlFileName.split('.')?.[0], imgUrlFileName) : of(undefined),
      seatingPlanUrl: event.seatingPlanUrl ? this.uploaderService.postImageUrl(event.imgUrl, seatingPlanUrlFileName.split('.')?.[0], seatingPlanUrlFileName) : of(undefined),
    }).pipe(
      switchMap( ({postalAddress, imgUrl, seatingPlanUrl}) => {
        if (postalAddress) {
          event.postalAddressId = postalAddress.id;
        } else {
          const postalError = `Erreur : adresse postale manquante pour ${event.name}`;
          this.notificationService.open(postalError);
          // eslint-disable-next-line no-console
          console.log(postalError);
        }
        event.imgUrl = imgUrl?.downloadUrl;
        event.seatingPlanUrl = seatingPlanUrl?.downloadUrl;
        return this.createEvent(event);
      }),
      switchMap( (createdEvent) => {
        eventPersonalization.eventId = createdEvent.id;
        eventPersonalization.id = null;
        if (event.WaitListConfig) {
          event.WaitListConfig.id = createdEvent.WaitListConfig?.id;
          event.WaitListConfig.eventId = createdEvent.id;
        }
        ticketTemplates?.forEach( (t) => {
          t.id = null;
          t.eventId = createdEvent.id;
        });
        ticketingPlatforms?.forEach( (t) => {
          t.id = null;
          t.eventId = createdEvent.id;
        });
        return forkJoin({
          eventPersonalization: eventPersonalization ?
            // eslint-disable-next-line max-len
            this.dashboardEventService.updateEventPersonalization(createdEvent.id, eventPersonalization) : of(undefined),
          eventTicketTemplates: ticketTemplates ?
            this.updateEventTicketTemplates(createdEvent.id, ticketTemplates) : of(undefined),
          eventTicketingPlatforms: ticketingPlatforms ?
            // eslint-disable-next-line max-len
            this.dashboardEventService.updateEventTicketingPlatforms(createdEvent.id, ticketingPlatforms) : of(undefined),
          eventWaitListConfig: event.WaitListConfig ?
            this.dashboardWaiListService.updateConfig(createdEvent.id, event.WaitListConfig) : of(undefined),
        }).pipe(
          map( (updates) => {
            createdEvent.EventPersonalization = updates.eventPersonalization;
            createdEvent.TicketTemplates = updates.eventTicketTemplates;
            createdEvent.TicketingPlatforms = updates.eventTicketingPlatforms;
            createdEvent.WaitListConfig = updates.eventWaitListConfig?.[0];
            return createdEvent;
          }),
        );
      }),
      switchMap( (createdEvent) => {
        if (!categoriesGroup || categoriesGroup.length <= 0) {
          return of(createdEvent);
        }
        categoriesGroup.forEach( (c) => {
          c.eventId = createdEvent.id;
        });
        // concat will keep only 1 createCategoriesGroup active at a time
        return concat(
          ...categoriesGroup.map( (g) => this.createCategoriesGroup(createdEvent.id, g) ),
        ).pipe(
          // reduces all emited values into 1 object, emited when the observable completes
          reduce( (groups, group) => {
            groups.push(group);
            return groups;
          }, [] as CategoriesGroup[]),
          map( (categoriesGroup) => {
            createdEvent.CategoriesGroups = categoriesGroup;
            return createdEvent;
          }),
        );
      }),
      switchMap( (createdEvent) => {
        if (!categories || categories.length <= 0) {
          return of(new Event(createdEvent));
        }
        categories.forEach( (c) => {
          delete c.id;
          c.eventId = createdEvent.id;
          c.ticketTemplateId = createdEvent.TicketTemplates?.[0]?.id;
          const group = createdEvent.CategoriesGroups?.find(
            (g) => g.platformsForeignIds[platform] === c.categoriesGroupId?.toString(),
          );
          if (group) {
            c.categoriesGroupId = group.id;
          } else {
            c.categoriesGroupId = undefined;
          }
        });
        return this.updateEventCategories(createdEvent.id, categories, createdEvent.CategoriesGroups).pipe(
          map( (update) => {
            createdEvent.Categories = update.updatedCategories.map( (c) => new Category(c));
            createdEvent.CategoriesGroups = createdEvent.CategoriesGroups
              .map( (g) => new CategoriesGroup(g, createdEvent.Categories));
            return new Event(createdEvent);
          }),
        );
      }),
      switchMap( (createdEvent) => {
        if (!event.Automations || event.Automations.length <= 0) {
          return of(new Event(createdEvent));
        }
        event.Automations.forEach( (a) => {
          a.eventId = createdEvent.id;
          a.id = null;
        });
        return zip(
          ...(event.Automations.map( (a) => this.automationService.upsertAutomation(createdEvent.id, a))),
        ).pipe(
          map( (automations) => {
            createdEvent.Automations = automations;
            return new Event(createdEvent);
          }),
        );
      }),
    );
  }

  updateEvent(event: Event) {
    if (!event ) {
      return undefined;
    }
    const route = `${this.configUrl}e/a/events`;
    return this.http.put<Event>(route, event.getGeneralInformations()).pipe(
      first(),
    );
  }

  /**
   * Create or updates tickettemplates in an array for an event
   * @param eventId number
   * @param ticketTemplates Array<TicketTemplate>
   * @returns TicketTemplate[]
   */
  updateEventTicketTemplates(eventId: number, ticketTemplates: Array<TicketTemplate>) {
    const route = `${this.configUrl}e/a/${eventId}/ticketTemplates`;
    return this.http.put<Array<TicketTemplate>>(route, ticketTemplates).pipe(
      first(),
      map( (templates) => templates.map( (t) => new TicketTemplate(t))),
    );
  }

  createCategoriesGroup(eventId: number, categoriesGroup: Omit<Partial<CategoriesGroup>, 'id'>) {
    const route = `${this.configUrl}e/a/${eventId}/categoriesGroup`;
    return this.http.post<CategoriesGroup>(route, {
      ...categoriesGroup,
      eventId,
    }).pipe(
      first(),
      map( (c) => new CategoriesGroup(c)),
    );
  }

  updateEventCategories(eventId: number, categories: Array<Category>, categoriesGroups: Array<CategoriesGroup>) {
    const route = `${this.configUrl}e/a/${eventId}/categories`;
    return this.http.put<{
      updatedCategories: Category[],
      updatedGroups: CategoriesGroup[]
    }>(route, {
      categories,
      categoriesGroups,
    }).pipe(
      first(),
    );
  }

  getEventCustomFaq(eventId: number) {
    const route = `${this.configUrl}e/faq/${eventId}`;
    return this.http.get<EventCustomFaq>(route);
  }

  updateEventFaq(eventId: number, faq: any) {
    const route = `${this.configUrl}e/a/faq/${eventId}`;
    return this.http.put<any>(route, faq).pipe(
      first(),
    );
  }

  deleteEventCategory(eventId: number, categoryId: number) {
    const route = `${this.configUrl}e/a/${eventId}/categories/${categoryId}`;
    return this.http.delete(route).pipe(
      first(),
    );
  }

  deleteEventCategoriesGroup(eventId: number, groupId: number) {
    const route = `${this.configUrl}e/a/${eventId}/group/${groupId}`;
    return this.http.delete(route).pipe(
      first(),
    );
  }

  getOrganizersWithTemplate(eventId: number) {
    const route = `${this.configUrl}e/a/${eventId}/organizersWithTemplate`;
    return this.http.get<Organizer[]>(route).pipe(
      first(),
    );
  }

  updateEventParticipantList(eventId: string, list: ValidTicket[]) {
    const route = `${this.configUrl}e/a/${eventId}/updateAccessControlList`;
    const blobList = new Blob([JSON.stringify(list)], {type : 'application/json'});
    const formData: FormData = new FormData();
    formData.append('participantList', blobList);
    return this.http.put(route, formData).pipe(
      first(),
    );
  }

  updatePlatformCategories(eventId: string, platformCategories: PlatformCategories) {
    const route = `${this.configUrl}e/a/${eventId}/updatePlatformCategories`;
    return this.http.put<{
      inserted: [];
      error: unknown,
      duplicates: [],
      sucess: boolean;
    }>(route, platformCategories).pipe(
      first(),
    );
  }

  blacklistEventParticipantList(eventId: number, list: ValidTicket[]) {
    const route = `${this.configUrl}e/a/${eventId}/blacklistEventParticipantList`;
    const blobList = new Blob([JSON.stringify(list)], {type : 'application/json'});
    const formData: FormData = new FormData();
    formData.append('participantList', blobList);
    return this.http.put(route, formData).pipe(
      first(),
    );
  }

  searchPimsEvents(searchTerm) {
    const route = `${this.configUrl}w/a/searchPimsEvents/${searchTerm}`;
    return this.http.get<any>(route).pipe(
      first(),
    );
  }

  getPimsEventById(pimsEventId: any) {
    const route = `${this.configUrl}w/a/getPimsEvents/${pimsEventId}`;
    return this.http.get<{event, categories}>(route).pipe(
      first(),
    );
  }

  getWeezeventRates(weezOrganizerID, weezEventId) {
    const route = `${this.configUrl}w/a/weezeventRates/organizations/${weezOrganizerID}/events/${weezEventId}`;
    return this.http.get<IWeezeventRate[]>(route).pipe(
      first(),
    );
  }

  checkEventsDoesNotExist(platform: Platforms, platformEventIds: string[]) {
    const route = `${this.configUrl}w/a/parterEvents/${platform}`;

    return this.http.post<Array<{
      id: number,
      name: string,
      TicketingPlatforms: {platformEventId: number}[]
    }>>(route, {partnerEventIdList: platformEventIds});
  }

}
